import { useSharedListIds } from "../../../hooks/queries/useSharedListIds";
import { canMoveOrDelete } from "../../../lib/listSharing";
import { List, NewTask, Task } from "../../../types";
import { DEFAULT_LIST_EMOJI } from "../../../constants/defaults";
import { useListMoveConfirm } from "./useListMoveConfirm";
import ListMoveConfirmDialog from "./ListMoveConfirmDialog";
import AddedByNote from "./AddedByNote";

interface UseSharedListSyncArgs {
  mode: "add" | "edit";
  task: Task | NewTask;
  viewerId: string | undefined;
  lists: List[];
  // A plain (task: Task | NewTask) => void — TaskDialog's own onTaskChange
  // is a discriminated union of two narrower signatures instead (TS can't
  // narrow that across a function boundary the way it can inline), so the
  // caller wraps it once before passing it in. See TaskDialog.tsx's
  // `writeTask`.
  onTaskChange: (task: Task | NewTask) => void;
}

/**
 * Everything TaskDialog needs from Shared lists (#286) in one call, so the
 * dialog itself — pinned at its grandfathered file-size ceiling — only
 * wires a handful of return values rather than growing its own list-change
 * handlers, permission check, and two new render trees. Owns: committing a
 * list change (replacing the old inline onSelectList/clearList), the two
 * move confirmations (item 6), disabling the picker per ADR-0003 (a Member
 * may only move/clear a Task they authored), and "Added by" (item 5).
 */
export function useSharedListSync({
  mode,
  task,
  viewerId,
  lists,
  onTaskChange,
}: UseSharedListSyncArgs) {
  const { data: sharedListIds } = useSharedListIds();
  const shared = sharedListIds ?? new Set<string>();

  const commitSelectList = (l: List) => {
    const updated: Task | NewTask =
      mode === "edit"
        ? ({ ...task, list_id: l.id, list: l.name, listEmoji: l.emoji } as Task)
        : ({ ...task, list_id: l.id } as NewTask);
    onTaskChange(updated);
  };

  const commitClearList = () => {
    const updated: Task | NewTask =
      mode === "edit"
        ? ({ ...task, list_id: null, list: "", listEmoji: DEFAULT_LIST_EMOJI } as Task)
        : ({ ...task, list_id: null } as NewTask);
    onTaskChange(updated);
  };

  const { onSelectList, onClearList, pending, confirm, cancel } = useListMoveConfirm({
    mode,
    currentListId: task.list_id,
    viewerId,
    lists,
    sharedListIds: shared,
    onCommitSelect: commitSelectList,
    onCommitClear: commitClearList,
  });

  // ADR-0003's one restriction, which covers Move and Delete together: a
  // Member may do either to a Task they wrote, and to nobody else's.
  const mayMoveOrDelete =
    mode !== "edit" || !viewerId || canMoveOrDelete(task as Task, viewerId, lists);

  const disabledReason = mayMoveOrDelete
    ? null
    : "Only the person who added this task, or the list's owner, can move it.";

  return {
    onSelectList,
    onClearList,
    disabledReason,
    // The dialog's trash icon hides rather than explains itself: the
    // database refuses the delete silently, so a button left on screen
    // would look like it worked and then bring the Task back.
    canDelete: mayMoveOrDelete,
    confirmDialogNode: pending ? (
      <ListMoveConfirmDialog pending={pending} onConfirm={confirm} onCancel={cancel} />
    ) : null,
    addedByNode:
      mode === "edit" ? (
        <AddedByNote
          taskId={(task as Task).id}
          authorId={(task as Task).user_id}
          viewerId={viewerId}
        />
      ) : null,
  };
}
